import {noop} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {Node, NodeProps} from 'reactflow';

import {makeReactContext} from 'lib/makeReactContext';
import {SchemaById, LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {BenchlingNodeV2Status} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';

export enum TaskSchemaNodeSection {
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
}

export const TASK_SCHEMA_NODE_SECTION_PREFIX: Record<TaskSchemaNodeSection, string> = {
  [TaskSchemaNodeSection.INPUTS]: 'input',
  [TaskSchemaNodeSection.OUTPUTS]: 'output',
};

export const NODE_SECTION_PREFIX_TO_TASK_SCHEMA_NODE_SECTION: Record<string, TaskSchemaNodeSection> =
  Object.entries(TASK_SCHEMA_NODE_SECTION_PREFIX).reduce(
    (acc, [key, value]) => {
      acc[value] = key as TaskSchemaNodeSection;
      return acc;
    },
    {} as Record<string, TaskSchemaNodeSection>
  );

export enum NodeTransitionPosition {
  SOURCE = 'source',
  TARGET = 'target',
}

export interface NodeTransitionPositionConfig {
  nodeTransitionPosition: NodeTransitionPosition;
  indexInNodeTransitionPosition: number;
}

export enum ProcessFlowchartNodeSection {
  TASK_SCHEMAS = 'TASK_SCHEMAS',
  LOGIC = 'LOGIC',
  PIPELINES = 'PIPELINES',
}

export enum ProcessFlowchartNodeType {
  TASK_SCHEMA_NODE = 'TaskSchemaNode',
  ROUTER_NODE = 'RouterNode',
  STOP_NODE = 'StopNode',
  /**
   * A placeholder node represents a node in the process of being created.
   */
  PLACEHOLDER_NODE = 'PlaceholderNode',
}

export type UserFacingNodeTypes = Exclude<
  ProcessFlowchartNodeType,
  ProcessFlowchartNodeType.PLACEHOLDER_NODE
>;

export const NODE_TYPE_TO_NAME: Record<ProcessFlowchartNodeType, string> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: 'Task',
  [ProcessFlowchartNodeType.ROUTER_NODE]: 'Router',
  [ProcessFlowchartNodeType.STOP_NODE]: 'Stop',
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: 'Placeholder',
};

export const NODE_TYPE_TO_SECTION: Record<UserFacingNodeTypes, ProcessFlowchartNodeSection> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: ProcessFlowchartNodeSection.TASK_SCHEMAS,
  [ProcessFlowchartNodeType.ROUTER_NODE]: ProcessFlowchartNodeSection.LOGIC,
  [ProcessFlowchartNodeType.STOP_NODE]: ProcessFlowchartNodeSection.LOGIC,
};

export const NODE_SECTION_TO_HEADER_TITLE: Record<ProcessFlowchartNodeSection, string> = {
  [ProcessFlowchartNodeSection.TASK_SCHEMAS]: 'Task schemas',
  [ProcessFlowchartNodeSection.LOGIC]: 'Logic',
  [ProcessFlowchartNodeSection.PIPELINES]: 'Pipelines',
};

export interface TaskSchemaNodeRowData {
  id: string;
  isFlowchartTask: boolean;
  sectionName: string;
  sectionType: TaskSchemaNodeSectionType;
  fieldName: string;
  fieldIcon: BenchlingIconName;
  fieldTypeLabel: string | JSX.Element;
  isSelected?: boolean;
}

export interface TaskSchemaNodeSectionData {
  sectionName: string;
  sectionType: TaskSchemaNodeSectionType;
  sectionRows: Array<TaskSchemaNodeRowData>;
}

export interface TaskSchemaDomainData {
  id: string;
  nodeType: ProcessFlowchartNodeType;
  nodeName: string;
  nodeSecondaryInfo: string | undefined;
  taskSchemaId: string;
}

export interface TaskSchemaInteractionNodeData {
  onNodeHeaderClick: (nodeId: string) => void;
  nodeDetailsSidepanelContent: (props: TaskNodeDetailsSidepanelContentProps) => JSX.Element | null;
  onFieldClick: (nodeId: string, fieldId: string) => void;
}
/**
 * Static data that is used for converting from and persisting task schemas to the database.
 */
export interface TaskSchemaStaticNodeData extends TaskSchemaDomainData {
  sections: Array<TaskSchemaNodeSectionData>;
}

/**
 * Populated data to be passed to the base node renderer.
 *
 * Combines properties needed for flowchart visualization and interaction.
 */
export type TaskSchemaNodeData = TaskSchemaStaticNodeData &
  TaskSchemaInteractionNodeData & {
    isDisabled?: boolean;
    status?: BenchlingNodeV2Status;
    headerProps?: {
      title: string;
      leftHandle?: JSX.Element;
      rightHandle?: JSX.Element;
      [key: string]: unknown;
    };
    sections: Array<TaskSchemaNodeSectionData>;
  };
export type TaskSchemaNode = Node<TaskSchemaNodeData>;
/**
 * Props for the task schema node renderer
 */

export type TaskSchemaNodeRendererProps = NodeProps<TaskSchemaNodeData>;

export interface HandlesContextValue {
  handlesInUse: Array<string>;
}

/** Context to keep track of which handles are currently connected via at least one edge */
export const HandlesContext = makeReactContext<HandlesContextValue>(
  () => ({
    handlesInUse: [],
  }),
  'HandlesContext'
);

export interface TaskNodeDetailsSidepanelContentProps {
  selectedNode: TaskSchemaNode;
  selectedFieldId: string | null;
  flowchart: {
    nodes: Array<TaskSchemaNode>;
    edges: Array<FlowchartEdge>;
  };
  allSchemasById: SchemaById;
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  transitionSource: TransitionSource | null;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  onChangeCustomMappingData: (edgeId: string, newCustomMappingData: CustomMappingData) => void;
  onChangeFlowchart?: () => void;
  locked?: boolean;
  didInitialConfigHaveConditionalMapping: boolean;
}
/**
 * Grid spacing in pixels for the flowchart editor.
 * This is used to snap the nodes to a grid when dragging them around.
 * Larger numers = further distance between snap points
 */
const SNAP_TO_GRID_PIXEL_SPACING = 100;
/**
 * Horizontal grid spacing (px), vertical grid spacing (px)
 */
const SNAP_GRID: [number, number] = [SNAP_TO_GRID_PIXEL_SPACING, SNAP_TO_GRID_PIXEL_SPACING];

export const DEFAULT_FLOWCHART_PROPS = {
  proOptions: REACT_FLOW_PRO_OPTIONS,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: true,
  zoomOnPinch: true,
  // This overrides any default viewport settings, initializes canvas with all nodes in view
  // Within the bounds of the min/max zoom levels
  fitView: true,
  snapToGrid: true,
  snapGrid: SNAP_GRID,
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
};

export const READ_ONLY_FLOWCHART_PROPS = {
  nodesDraggable: false,
  nodesConnectable: false,
  elementsSelectable: true,
  onConnect: noop,
};

export const EDITABLE_FLOWCHART_PROPS = {
  nodesDraggable: true,
  nodesConnectable: true,
  selectNodesOnDrag: false,
  elementsSelectable: true,
};

export enum ProcessFlowchartEdgeType {
  CONTROL_FLOW = 'control-flow',
  DATA_FLOW = 'data-flow',
}

export interface FlowchartStaticEdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  type: ProcessFlowchartEdgeType;
}

export interface FlowchartEdge extends FlowchartStaticEdgeData {}

export enum TaskSchemaNodeSectionType {
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
  NOT_MAPPABLE = 'Not Mappable',
}
/**
 * The source of the transition.
 *
 * This is used to determine which objects will be used as the input to the mapper at execution time.
 * In the backend, this defines which object (the task or output) is used as the "token" that is processed by
 * the workflow orchestration system.
 *
 * - If the source is "inputs", then we create a single downstream element for every input to the source node.
 *   In other words, the "process task" is used as the token in workflow orchestration.
 * - If the source is "outputs", then we create a single downstream element for every outupt of the source node.
 *   In other words, the "process output" is used as the token in workflow orchestration.
 */

export enum TransitionSource {
  INPUTS = 'inputs',
  OUTPUTS = 'outputs',
}

export interface CustomMappingData {
  lookupConfig?: LookupConfigSpec;
  resultMustBeNonNullToMap?: boolean;
}

export function isCustomMappingDataEmpty(customMappingData: CustomMappingData): boolean {
  return customMappingData.lookupConfig == null && !customMappingData.resultMustBeNonNullToMap;
}

export const FLOWCHART_TASK_SECONDARY_INFO = 'Flowchart task';
export const FLOWCHART_OUTPUT_SECONDARY_INFO = 'Flowchart output';

export const PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES = StyleSheet.create({
  // TODO: Remove this once transition modal constants are consolidated with this file
  editorBodyWrapper: {
    overflowY: 'hidden',
  },
  // TODO: Remove this once transition modal constants are consolidated with this file
  reactFlowCanvas: {
    display: 'flex',
    width: '100%',
    /**
     * Reactflow's parent must have a fixed height, but this component
     * gets used in a modal where the height is just 100%.
     * So we are using viewport height here to make it responsive while still a "fixed" height
     * The additional 250px is to account for the header and footer of the modal that will
     * also be taken up by the viewport.
     */
    height: 'calc(100vh - 250px)',
  },
  flowchartEditorContentWrapper: {
    display: 'flex',
    /**
     * Reactflow's parent must have a fixed height, but this component
     * gets used in a parent where the height is just 100%.
     * So we are using viewport height here to make it responsive while still a "fixed" height
     * The additional 250px is to account for additional components in the viewport.
     */
    height: 'calc(100vh - 250px)',
    width: '100%',
  },
  flowchartEditorContent: {
    display: 'flex',
    height: '100%',
    position: 'relative',
  },
});
// Allows for filtering out interaction fields from static node data
// When a new field is added to TaskSchemaInteractionNodeData, will get compilation error here
type InteractionKeys = keyof TaskSchemaInteractionNodeData;
const interactionFields: Record<InteractionKeys, undefined> = {
  onNodeHeaderClick: undefined,
  nodeDetailsSidepanelContent: undefined,
  onFieldClick: undefined,
};
export const INTERACTION_FIELDS = Object.keys(interactionFields);
